/// <reference types="youtube" />
/* eslint-disable no-undef */

import React, { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { loadYouTubeAPI, isYouTubeAPIReady } from './youtubeApiLoader';
import { extractYouTubeVideoId, getYouTubeEmbedUrl } from './utils';
import { YouTubePlayerProps, YouTubePlayerState, VideoPlayerMethods } from './types';

/**
 * YouTube Player Component
 * Wraps the YouTube iframe API to provide a React component interface
 */
const YouTubePlayer = forwardRef<VideoPlayerMethods, YouTubePlayerProps>(({
  videoUrl,
  onTimeUpdate,
  onStateChange,
  onReady,
  onError,
  autoplay = false,
  startTime,
  className = '',
  width = '100%',
  height = '100%',
  controls = true,
  modestbranding = true,
  rel = false,
  showinfo = false,
}, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YT.Player | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [useFallback, setUseFallback] = useState(false);
  const timeUpdateIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const loadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Expose player methods through ref
  useImperativeHandle(ref, () => ({
    play: () => {
      try {
        playerRef.current?.playVideo();
      } catch (error) {
        console.warn('Error playing video:', error);
      }
    },
    pause: () => {
      try {
        playerRef.current?.pauseVideo();
      } catch (error) {
        console.warn('Error pausing video:', error);
      }
    },
    seekTo: (seconds: number) => {
      try {
        playerRef.current?.seekTo(seconds, true);
      } catch (error) {
        console.warn('Error seeking video:', error);
      }
    },
    getCurrentTime: () => {
      try {
        return playerRef.current?.getCurrentTime() || 0;
      } catch (error) {
        console.warn('Error getting current time:', error);
        return 0;
      }
    },
    getDuration: () => {
      try {
        return playerRef.current?.getDuration() || 0;
      } catch (error) {
        console.warn('Error getting duration:', error);
        return 0;
      }
    },
    setVolume: (volume: number) => {
      try {
        playerRef.current?.setVolume(Math.max(0, Math.min(100, volume)));
      } catch (error) {
        console.warn('Error setting volume:', error);
      }
    },
    getVolume: () => {
      try {
        return playerRef.current?.getVolume() || 0;
      } catch (error) {
        console.warn('Error getting volume:', error);
        return 0;
      }
    },
    setPlaybackRate: (rate: number) => {
      try {
        playerRef.current?.setPlaybackRate(rate);
      } catch (error) {
        console.warn('Error setting playback rate:', error);
      }
    },
    getPlaybackRate: () => {
      try {
        return playerRef.current?.getPlaybackRate() || 1;
      } catch (error) {
        console.warn('Error getting playback rate:', error);
        return 1;
      }
    },
    mute: () => {
      try {
        playerRef.current?.mute();
      } catch (error) {
        console.warn('Error muting video:', error);
      }
    },
    unMute: () => {
      try {
        playerRef.current?.unMute();
      } catch (error) {
        console.warn('Error unmuting video:', error);
      }
    },
    isMuted: () => {
      try {
        return playerRef.current?.isMuted() || false;
      } catch (error) {
        console.warn('Error checking mute status:', error);
        return false;
      }
    },
  }));
  
  // Store props in refs to access latest values without causing re-initializations
  const propsRef = useRef({ autoplay, startTime, controls, modestbranding, rel, showinfo, width, height, onReady, onStateChange, onError });
  useEffect(() => {
    propsRef.current = { autoplay, startTime, controls, modestbranding, rel, showinfo, width, height, onReady, onStateChange, onError };
  }, [autoplay, startTime, controls, modestbranding, rel, showinfo, width, height, onReady, onStateChange, onError]);

  // Extract video ID from URL
  const videoId = extractYouTubeVideoId(videoUrl);
  const embedUrl = videoId ? getYouTubeEmbedUrl(videoUrl) : null;

  // Component lifecycle logging (only in development)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[YouTubePlayer] Component mounted', { videoId });
    }
    return () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[YouTubePlayer] Component unmounting', { videoId });
      }
    };
  }, []);

  // Initialize YouTube player - only re-initialize when videoId changes
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[YouTubePlayer] Initializing player', { videoId });
    }
    
    if (!videoId || !embedUrl) {
      console.warn('YouTubePlayer: Missing videoId or embedUrl', { videoId, embedUrl });
      return;
    }

    let mounted = true;
    let playerInstance: YT.Player | null = null;

    // Clean up any existing player first to avoid DOM conflicts
    const cleanupExistingPlayer = () => {
      const existingPlayer = playerRef.current;
      if (existingPlayer) {
        try {
          if (typeof existingPlayer.destroy === 'function') {
            existingPlayer.destroy();
          }
        } catch (error) {
          console.warn('Error cleaning up existing player (safe to ignore):', error);
        }
        playerRef.current = null;
      }
      
      // Clear container to ensure clean state
      if (containerRef.current) {
        // Remove all children that YouTube API might have added
        while (containerRef.current.firstChild) {
          containerRef.current.removeChild(containerRef.current.firstChild);
        }
      }
    };

    // Clean up before initializing new player
    cleanupExistingPlayer();

    const initializePlayer = async () => {
      try {
        setIsLoading(true);
        setError(null);
        setUseFallback(false);

        // Set a timeout to fallback to basic iframe if player doesn't initialize
        loadingTimeoutRef.current = setTimeout(() => {
          if (mounted) {
            console.warn('YouTube player initialization timeout - falling back to basic iframe');
            setIsLoading(false);
            setUseFallback(true);
          }
        }, 10000); // 10 second timeout

        // Load YouTube API if not ready
        if (!isYouTubeAPIReady()) {
          await loadYouTubeAPI();
        }

        // Wait a bit to ensure container is mounted and clean
        let retries = 0;
        while (!containerRef.current && retries < 10) {
          await new Promise(resolve => setTimeout(resolve, 100));
          retries++;
        }

        if (!mounted || !containerRef.current) {
          console.error('YouTubePlayer: Container not available after retries', { retries, hasContainer: !!containerRef.current });
          if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
          }
          setIsLoading(false);
          setError('Container element not found');
          return;
        }

        // Ensure container is empty before YouTube API uses it
        // Use a safe method that checks if child exists before removing
        if (containerRef.current) {
          try {
            let firstChild: ChildNode | null;
            while ((firstChild = containerRef.current.firstChild)) {
              if (firstChild && firstChild.parentNode === containerRef.current) {
                containerRef.current.removeChild(firstChild);
              } else {
                break; // Child was already removed by React
              }
            }
          } catch (error) {
            // Container may have been removed by React - that's okay
            console.warn('Container cleanup warning (safe to ignore):', error);
          }
        }

        if (process.env.NODE_ENV === 'development') {
          console.log('[YouTubePlayer] Container ready, creating player instance', { videoId });
        }

        // Get latest props from ref
        const props = propsRef.current;
        
        // Create player instance and store it
        playerInstance = new window.YT.Player(containerRef.current, {
          videoId: videoId,
          width: typeof props.width === 'number' ? props.width : undefined,
          height: typeof props.height === 'number' ? props.height : undefined,
          playerVars: {
            autoplay: props.autoplay ? 1 : 0,
            controls: props.controls ? 1 : 0,
            modestbranding: props.modestbranding ? 1 : 0,
            rel: props.rel ? 1 : 0,
            showinfo: props.showinfo ? 1 : 0,
            start: props.startTime ? Math.floor(props.startTime) : undefined,
            enablejsapi: 1,
            // Use the full origin including protocol to avoid postMessage errors
            origin: window.location.protocol + '//' + window.location.host,
            // Modern YouTube player features
            playsinline: 1,
            iv_load_policy: 3, // Hide annotations
          },
          events: {
            onReady: (event: { target: YT.Player }) => {
              if (!mounted) return;
              if (process.env.NODE_ENV === 'development') {
                console.log('[YouTubePlayer] Player ready', { videoId });
              }
              if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
                loadingTimeoutRef.current = null;
              }
              setIsLoading(false);
              playerRef.current = event.target;
              playerInstance = event.target;
              
              // Seek to start time if specified (use latest from ref)
              const currentProps = propsRef.current;
              if (currentProps.startTime !== undefined && currentProps.startTime > 0) {
                event.target.seekTo(currentProps.startTime, true);
              }

              currentProps.onReady?.();
            },
            onStateChange: (event: { data: YT.PlayerState; target: YT.Player }) => {
              if (!mounted) return;
              const state = event.data as unknown as YouTubePlayerState;
              const currentProps = propsRef.current;
              currentProps.onStateChange?.(state);

              // Start/stop time update polling based on playback state
              if (state === YouTubePlayerState.PLAYING) {
                startTimeUpdatePolling(event.target);
              } else {
                stopTimeUpdatePolling();
              }
            },
            onError: (event: { data: YT.PlayerError }) => {
              if (!mounted) return;
              console.error('YouTube player error:', event.data);
              if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
              }
              setIsLoading(false);
              setError(`YouTube player error: ${event.data}`);
              const err = new Error(`YouTube player error: ${event.data}`);
              const currentProps = propsRef.current;
              currentProps.onError?.(err);
            },
          },
        });
      } catch (error) {
        if (!mounted) return;
        console.error('Error initializing YouTube player:', error);
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
        }
        setIsLoading(false);
        const errorMessage = error instanceof Error ? error.message : 'Failed to initialize YouTube player';
        setError(errorMessage);
        const err = error instanceof Error ? error : new Error('Failed to initialize YouTube player');
        onError?.(err);
      }
    };

    // Small delay to ensure React has finished reconciling before we manipulate DOM
    const initTimeout = setTimeout(() => {
      initializePlayer();
    }, 0);

    return () => {
      mounted = false;
      clearTimeout(initTimeout);
      stopTimeUpdatePolling();
      
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      
      // Clean up player instance - defer to avoid React DOM conflicts
      const playerToDestroy = playerInstance || playerRef.current;
      if (playerToDestroy) {
        // Store reference and clear refs immediately to prevent React conflicts
        playerRef.current = null;
        playerInstance = null;
        
        // Defer destruction to next event loop to let React finish unmounting
        setTimeout(() => {
          try {
            if (playerToDestroy && typeof playerToDestroy.destroy === 'function') {
              playerToDestroy.destroy();
            }
          } catch (error) {
            // Ignore - player may already be destroyed by YouTube API or React
            console.warn('Error destroying YouTube player (safe to ignore):', error);
          }
        }, 0);
      }
      
      // Don't manually manipulate container DOM - let React handle it completely
      // The key prop on VideoPlayer will force React to recreate the entire component tree
    };
  }, [videoId, embedUrl]); // Only depend on videoId and embedUrl to minimize re-initializations

  // Handle video URL changes
  useEffect(() => {
    if (!playerRef.current || !videoId) {
      return;
    }

    const newVideoId = extractYouTubeVideoId(videoUrl);
    if (newVideoId && newVideoId !== videoId) {
      setIsLoading(true);
      playerRef.current.loadVideoById(newVideoId);
    }
  }, [videoUrl, videoId]);

  // Time update polling
  const startTimeUpdatePolling = useCallback((player: YT.Player): void => {
    stopTimeUpdatePolling(); // Clear any existing interval

    timeUpdateIntervalRef.current = setInterval(() => {
      try {
        const currentTime = player.getCurrentTime();
        if (currentTime !== undefined && currentTime !== null) {
          onTimeUpdate?.(currentTime);
        }
      } catch (error) {
        console.warn('Error getting current time:', error);
      }
    }, 100); // Update every 100ms for smooth time tracking
  }, [onTimeUpdate]);

  const stopTimeUpdatePolling = useCallback(() => {
    if (timeUpdateIntervalRef.current) {
      clearInterval(timeUpdateIntervalRef.current);
      timeUpdateIntervalRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopTimeUpdatePolling();
    };
  }, [stopTimeUpdatePolling]);

  if (!videoId || !embedUrl) {
    console.warn('YouTubePlayer: Invalid video URL', { videoUrl, videoId, embedUrl });
    return (
      <div className={`flex items-center justify-center bg-gray-100 ${className}`}>
        <p className="text-gray-500 text-sm">Invalid YouTube URL</p>
      </div>
    );
  }

  // Fallback to basic iframe if API fails or times out
  if (useFallback && embedUrl) {
    return (
      <div className={`relative ${className}`} style={{ width, height }}>
        <iframe
          src={embedUrl}
          className="w-full h-full"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title="YouTube video player"
        />
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} style={{ width, height }}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
          <div className="text-gray-500 text-sm">Loading video...</div>
        </div>
      )}
      {error && !useFallback && (
        <div className="absolute inset-0 flex items-center justify-center bg-red-50 z-10">
          <div className="text-red-600 text-sm text-center px-4">
            <p className="font-medium">Error loading video</p>
            <p className="text-xs mt-1">{error}</p>
          </div>
        </div>
      )}
      {/* 
        Key prop forces React to recreate this div when videoId changes,
        ensuring clean state. React should not manage children of this div
        as YouTube API will directly manipulate it.
      */}
      <div 
        key={`youtube-player-${videoId}`}
        ref={containerRef} 
        className="w-full h-full"
        suppressHydrationWarning
      />
    </div>
  );
});

// Export player methods interface for external access
export type { VideoPlayerMethods };

YouTubePlayer.displayName = 'YouTubePlayer';

export default YouTubePlayer;

